import { createSelector } from 'reselect';
import sortOrderTypes from '../../constants/sortOrderTypes';
import filterTypes from '../../constants/filterTypes';

// Input selectors
const selectSortOrder = state => state.sort.sortOrder;
const selectPackages = state => state.priceOfferList.packages;
const selectSelectedFilters = state => state.filter.selectedFilters;
const selectFilterOptions = state => state.filter.filterOptions;

// Filter function
const filterFunction = (selectedFilters, skipFilterType = null) => (pkg) => {
  // Selected filters
  const { selectedDepartureMonths, selectedDepartureAirports, selectedMaxPrice, selectedDestinations } = selectedFilters;

  // Package filter value
  const packageDepartureMonth = pkg?.filterValues?.departureMonth?.value;
  const packageDepartureAirport = pkg?.filterValues?.departureAirport?.value;
  const packagePrice = pkg?.filterValues?.price;
  const packageDestinations = pkg?.filterValues?.destination;
  const packageDestinationIds = (packageDestinations || []).map(currentDestination => currentDestination.id);

  // Check if OK (should be visible)
  const departureMonthOK =
    skipFilterType === filterTypes.departureMonth ||
    selectedDepartureMonths.length === 0 ||
    selectedDepartureMonths.includes(packageDepartureMonth);

  const departureAirportOK =
    skipFilterType === filterTypes.departureAirport ||
    selectedDepartureAirports.length === 0 ||
    selectedDepartureAirports.includes(packageDepartureAirport);

  const priceOK =
    skipFilterType === filterTypes.price ||
    selectedMaxPrice === null ||
    packagePrice <= selectedMaxPrice;

  const destinationOK =
    skipFilterType === filterTypes.destination ||
    selectedDestinations.length === 0 ||
    selectedDestinations.some(currentDestination => packageDestinationIds.includes(currentDestination.id));

  return departureMonthOK && departureAirportOK && priceOK && destinationOK;
};

// Sort functions
const sortByEarliestDate = (a, b) => {
  return a.sortValues.departureDate - b.sortValues.departureDate;
};

const sortByHighestRating = (a, b) => {
  return b.sortValues.rating - a.sortValues.rating;
};

const sortByLowestPrice = (a, b) => {
  return a.sortValues.price - b.sortValues.price;
};

const sortByHighestDiscount = (a, b) => {
  return b.sortValues.discount - a.sortValues.discount;
};

// Memoized selectors
const selectFilteredPackages = createSelector(
  [selectSelectedFilters, selectPackages],
  (selectedFilters, packages) => {
    return packages.filter(filterFunction(selectedFilters));
  },
);

export const selectSortedAndFilteredPackages = createSelector(
  [selectSortOrder, selectFilteredPackages],
  (sortOrder, packages) => {
    switch (sortOrder) {
      case sortOrderTypes.highestDiscount:
        return [...packages].sort(sortByHighestDiscount);
      case sortOrderTypes.lowestPrice:
        return [...packages].sort(sortByLowestPrice);
      case sortOrderTypes.highestRating:
        return [...packages].sort(sortByHighestRating);
      case sortOrderTypes.earliestDate:
      default:
        return [...packages]?.sort(sortByEarliestDate);
    }
  },
);

export const selectPotentialNumberOfPackages = createSelector(
  [selectPackages, selectSelectedFilters, selectFilterOptions],
  (packages, selectedFilters, filterOptions) => {
    const { departureMonthOptions, departureAirportOptions, idToDestinationMap } = filterOptions;

    const departureMonthPotentials = {};
    departureMonthOptions.forEach((month) => {
      const tempPackages = packages.filter(filterFunction(selectedFilters, filterTypes.departureMonth));
      const numberOfHits = tempPackages.filter(pkg => pkg?.filterValues?.departureMonth?.value === month.value).length;
      departureMonthPotentials[month.value] = numberOfHits;
    });

    const departureAirportPotentials = {};
    departureAirportOptions.forEach((airport) => {
      const tempPackages = packages.filter(filterFunction(selectedFilters, filterTypes.departureAirport));
      const numberOfHits = tempPackages.filter(pkg => pkg?.filterValues?.departureAirport?.value === airport.value)?.length;
      departureAirportPotentials[airport.value] = numberOfHits;
    });

    const destinationPotentials = {};
    Object.keys(idToDestinationMap).forEach((destinationId) => {
      const tempPackages = packages.filter(filterFunction(selectedFilters, filterTypes.destination));
      const numberOfHits = tempPackages.filter((pkg) => {
        const packageDestinationIds = (pkg?.filterValues?.destination || []).map(currentDestination => currentDestination.id);
        return packageDestinationIds.includes(destinationId);
      })?.length;
      destinationPotentials[destinationId] = numberOfHits;
    });

    return {
      departureMonthPotentials,
      departureAirportPotentials,
      destinationPotentials,
    };
  },
);
